import React from 'react';
import {
  SelectChangeEvent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Typography
} from '@mui/material';
import DateTimePicker from 'react-datetime-picker';
import { supportedTimeFrames } from '@components/AdminSlugActions/utils';
import { isInternalLinkTextValid } from '@utils/linkUtils';

export interface ItemType {
  name: string;
  value: string | number;
}

const renderItems = (items: ItemType[]) => {
  return items.map((section) => (
    <MenuItem key={section.value} value={section.value}>
      {section.name}
    </MenuItem>
  ));
};

export const renderSelect = (
  label: string,
  options: ItemType[],
  value: string | number,
  onChange: (e: SelectChangeEvent<string | number>) => void,
  emptyItemName?: string,
  backgroundColor: string = 'transparent'
) => {
  return (
    <FormControl margin="normal" fullWidth>
      <InputLabel id={`id-${label}`} sx={{ backgroundColor }}>
        {label}
      </InputLabel>
      <Select
        labelId={`id-${label}`}
        value={value}
        onChange={onChange}
        displayEmpty
      >
        {emptyItemName ? (
          <MenuItem value="" disabled>
            {emptyItemName}
          </MenuItem>
        ) : null}
        {renderItems(options)}
      </Select>
    </FormControl>
  );
};

export const renderCheckbox = (
  checked: boolean,
  onClick: () => void,
  label: string
) => {
  return (
    <FormControlLabel
      control={<Checkbox checked={checked} onClick={onClick} />}
      label={label}
    />
  );
};

export const renderTextEdit = (
  value: string,
  onChange: (value: string) => void,
  label: string
) => {
  return (
    <FormControl margin="normal" fullWidth>
      <TextField
        fullWidth
        label={label}
        value={value || ''}
        autoComplete="off"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
      />
    </FormControl>
  );
};

export const renderPostDate = (
  postDate: Date,
  setPostDate: (date: Date) => void
) => {
  return (
    <FormControl margin="normal" fullWidth>
      <Box>
        <Typography variant="overline">Post date</Typography>
        <Box>
          <DateTimePicker
            clearIcon={null}
            onChange={setPostDate}
            value={postDate}
          />
        </Box>
      </Box>
    </FormControl>
  );
};

export const renderSelectActiveTimeFrame = (
  activeTimeFrame: string,
  setActiveTimeFrame: (value: string) => void
) => {
  return renderSelect(
    'Active time frame',
    supportedTimeFrames,
    activeTimeFrame,
    (e: SelectChangeEvent<string>) => setActiveTimeFrame(e.target.value),
    'Select active time frame'
  );
};

export const isSaveDisabled = ({
  slug,
  title,
  type,
  menuTitle,
  dealListId,
  brand,
  category,
  keyword,
  strategyId,
  internalLinkText
}: {
  slug: string;
  title: string;
  type: string;
  menuTitle: string;
  dealListId: number | null;
  brand: string | null;
  category: string | null;
  keyword: string | null;
  strategyId: string | null;
  internalLinkText: string;
}) => {
  if (!isInternalLinkTextValid(internalLinkText)) {
    return true;
  }

  // Check for empty required fields
  if (!slug || !title || !type || !menuTitle) {
    // save is disabled
    return true;
  }

  // Additional conditions based on the 'type' value
  switch (type) {
    case 'DEAL_LIST':
      return !dealListId;
    case 'BRAND':
    case 'TOY_DEAL_BRAND':
      return !brand;
    case 'CATEGORY':
    case 'TOP_CHEAP_CATEGORY_DEALS_UNDER_3':
    case 'TOY_DEAL_SECOND_CATEGORY':
      return !category;
    case 'GIFT_GUIDE':
      return !strategyId;
    case 'TOY_DEAL_KEYWORD':
    case 'TOY_DEAL_CATEGORY_KEYWORD':
      return !keyword;
    default:
      return false;
  }
};
